import type { BaseModelAttributes } from './base';
import type { Resolution, AspectRatio } from './common';
import type { ScriptMode } from './scriptMode';

/**
 * 剧本语言类型
 */
export type ScriptLanguage = 'zh-CN' | 'en-US';



/**
 * 剧本亮点接口
 */
export interface ScriptHighlight {
  name: string;           // 亮点名称
  description: string;    // 亮点介绍
}

/**
 * 角色/主体接口
 */
export interface ScriptCharacter {
  id?: string;            // 唯一标识（格式：char_001），用于稳定引用，向后兼容
  name: string;           // 角色名称
  description?: string;   // 角色描述（用于展示）
  prompt: string;         // AI提示词（用于图片生成）
  imageUrl?: string;      // 角色图片地址
  audioUrl?: string;      // 参考音频URL
  voiceId?: string;       // 音色ID
  assetId?: string;       // 系统主体资源标识符（如预定义角色库 ID），用于关联公共主体
  sourceAssetId?: string; // 源主体 ID（当由其他 ProjectAsset 拷贝而来时指向源 ID）
}

/**
 * 道具接口
 */
export interface ScriptProp {
  id?: string;            // 唯一标识（格式：prop_001），用于稳定引用，向后兼容
  name: string;           // 道具名称
  description?: string;   // 道具描述（用于展示）
  prompt: string;         // AI提示词（用于图片生成）
  imageUrl?: string;      // 道具图片地址
  assetId?: string;       // 系统主体资源标识符
  sourceAssetId?: string; // 源主体 ID
}

/**
 * 场景接口
 * 
 * 场景粒度为"地点+视角/状态"，同一地点的不同视角/时间状态作为独立场景条目。
 * 例如：咖啡厅-全景、咖啡厅-特写、咖啡厅-夜晚 各为独立场景，共享相同基础 prompt。
 */
export interface ScriptScene {
  id?: string;            // 唯一标识（格式：scene_001），用于稳定引用，向后兼容
  name: string;           // 场景名称，格式建议：{地点}-{视角/状态}，如"咖啡厅-全景"
  location?: string;      // 所属地点标识，同一地点的不同视角/状态共享此值，用于宫格图分组生成
  description?: string;   // 场景描述（用于展示）
  prompt: string;         // AI提示词（用于图片生成），同地点变体共享基础描述+追加视角词
  imageUrl?: string;      // 场景图片地址
  assetId?: string;       // 系统主体资源标识符
  sourceAssetId?: string; // 源主体 ID
}

/**
 * 分镜接口
 */
export interface ScriptShot {
  description: string;    // 画面描述
  duration: number;       // 画面时长（秒）
  structure: string;      // 结构设计
  cameraWork: string;     // 运镜调度
  sceneName: string;      // 场景名称（必须与scenes中的某个场景名称一致）
  voiceRole?: string;     // 配音角色（必须是角色名称或"旁白"）
  dialogue?: string;      // 台词内容
  emotion?: string;       // 台词情绪
  subjects: string[];     // 主体名称数组
  props: string[];        // 道具名称数组
}

/**
 * 剧本生成状态枚举
 */
export enum ScriptGenerationStatus {
  IDLE = 'idle',                 // 空闲（未开始生成）
  PROCESSING = 'processing',     // 生成中
  COMPLETED = 'completed',       // 已完成
  FAILED = 'failed',            // 失败
  CANCELLED = 'cancelled'        // 已取消
}

/**
 * 图片生成任务状态
 */
export interface ImageGenerationTaskStatus {
  name: string;                  // 角色/道具/场景名称
  taskId: string;                // 图片生成任务ID
  status: 'pending' | 'processing' | 'completed' | 'failed';
  imageUrl?: string;             // 生成的图片URL
  error?: string;                // 错误信息
}

/**
 * 子步骤状态
 */
export interface GenerationSubStepStatus {
  name: 'design' | 'generate_images' | 'extract';  // 子步骤名称
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;                     // 进度 0-100
  imageGenerationTasks?: ImageGenerationTaskStatus[];  // 图片生成任务详情
  error?: string;
}

/**
 * 步骤状态
 */
export interface GenerationStepStatus {
  step: number;                        // 步骤编号 (1-6)
  name: string;                        // 步骤名称
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;                    // 总进度 0-100
  subSteps?: GenerationSubStepStatus[];  // 子步骤（仅步骤3,4,5使用）
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
}

/**
 * 剧集剧本主接口
 */
export interface IEpisodeScript extends BaseModelAttributes {
  userId: string;
  projectId: string;
  episodeId: string;

  /**
   * @deprecated 将在迁移后移除，1:1 关系不需要此字段
   * 迁移脚本会处理移除此字段
   */
  isActive: boolean;

  // 分步骤生成流程状态
  steps: ScriptStep[];

  // 视频配置
  imageModelId?: string;
  aspectRatio: AspectRatio;
  resolution: Resolution;
  bgmId?: string;
  bgmUrl?: string;

  // 内容
  title?: string;          // 故事标题
  welcomeMessage?: string; // 欢迎语
  originalPrompt?: string; // 原始提示词
  prompt?: string;         // 当前提示词
  summary?: string;
  shotsSummary?: string;   // 分镜概要
  styleName?: string;      // 风格名称（如：3D卡通风格）
  artStyle?: string;       // 风格说明介绍
  targetDuration?: number; // 用户期望视频时长（秒），如 15/30/60/90/120
  basePrompt?: string;     // 风格提示词（如：3D cartoon style, vibrant colors）

  // 剧本模式相关
  scriptMode: ScriptMode;              // 剧本模式
  uploadedScriptContent?: string;      // 上传内容的解析结果（FILE_SCRIPT: 分镜文本；MV: AudioAnalysisResult JSON）
  uploadedFileUrl?: string;            // 上传文件的原始 URL（FILE_SCRIPT: 分镜脚本文件；MV: 音频文件）
  projectScriptId?: string;            // 关联的项目剧本章节ID（PROJECT_SCRIPT 模式使用）
  scriptIntentReasoning?: string;      // 意图识别理由（已废弃）

  // 语言设置
  language?: ScriptLanguage;           // 剧本语言（zh-CN 或 en-US）

  // 音频资源
  narratorVoiceUrl?: string;  // 旁白音色URL

  // 结构化数据
  highlights: ScriptHighlight[];
  characters: ScriptCharacter[];
  props: ScriptProp[];
  scenes: ScriptScene[];
  shots: ScriptShot[];

  // 任务状态相关
  generationStatus: ScriptGenerationStatus;  // 生成总体状态
  currentGenerationStep: number;             // 当前执行到第几步 (0-6)
  generationStepStatus: GenerationStepStatus[];  // 每个步骤的状态
  generationTaskId?: string;                 // Bull 任务ID
  generationErrorMessage?: string;           // 错误信息

  // 分镜配置
  maxShotDuration?: number; // 单个分镜最大时长（秒），NULL 表示不限制

  // 关联数据(可选)
  episode?: {
    id: string;
    title: string;
  };
}

/**
 * 创建剧集剧本请求
 */
export interface CreateEpisodeScriptRequest {
  projectId: string;
  episodeId: string;

  // 视频配置
  imageModelId?: string;
  aspectRatio?: AspectRatio;
  resolution?: Resolution;
  bgmId?: string;
  bgmUrl?: string;

  // 内容
  summary?: string;
  styleName?: string;      // 风格名称
  artStyle?: string;       // 风格说明
  basePrompt?: string;     // 风格提示词

  // 结构化数据
  highlights?: ScriptHighlight[];
  characters?: ScriptCharacter[];
  scenes?: ScriptScene[];
  props?: ScriptProp[];
  shots?: ScriptShot[];
}

/**
 * 更新剧集剧本请求
 */
export interface UpdateEpisodeScriptRequest {
  // 视频配置
  imageModelId?: string;
  aspectRatio?: AspectRatio;
  resolution?: Resolution;
  bgmId?: string;
  bgmUrl?: string;

  // 内容
  summary?: string;
  styleName?: string;      // 风格名称
  artStyle?: string;       // 风格说明
  basePrompt?: string;     // 风格提示词

  // 结构化数据
  highlights?: ScriptHighlight[];
  characters?: ScriptCharacter[];
  props?: ScriptProp[];
  scenes?: ScriptScene[];
}

/**
 * 更新分镜请求
 */
export interface UpdateEpisodeScriptShotRequest {
  description?: string;
  dialogue?: string;
  sceneName?: string;
  structure?: string;
  cameraWork?: string;
  voiceRole?: string;
  subjects?: string[];
  props?: string[];
}

/**
 * 剧集剧本查询参数
 */
export interface EpisodeScriptQuery {
  search?: string;
  projectId?: string;
  episodeId?: string;
  aspectRatio?: AspectRatio;
  resolution?: Resolution;
  generationStatus?: ScriptGenerationStatus;
}

/**
 * 启动剧本生成请求
 */
export interface GenerateEpisodeScriptRequest {
  prompt: string;                        // 用户需求描述
  scriptFileUrl?: string;                // 剧本文件URL（docx或txt）
  characters?: ScriptCharacter[];        // 用户预先指定的角色
  props?: ScriptProp[];                  // 用户预先指定的道具
  scenes?: ScriptScene[];                // 用户预先指定的场景
  artStyle?: string;                     // 用户指定的风格
  imageModelId?: string;                 // 图片生成模型
  llmModelId?: string;                   // LLM模型
  llmVlModelId?: string;                 // 视觉LLM模型
  aspectRatio?: AspectRatio;             // 画面比例
  resolution?: Resolution;               // 分辨率
  videoWidth?: number;                   // 视频宽度
  videoHeight?: number;                  // 视频高度
  projectId?: string;                    // 项目ID（可选，如果提供则不创建新项目）
}

/**
 * 剧本生成状态响应
 */
export interface EpisodeScriptGenerationStatusResponse {
  scriptId: string;
  generationStatus: ScriptGenerationStatus;
  currentGenerationStep: number;
  generationStepStatus: GenerationStepStatus[];

  // 已生成的内容
  title?: string;
  welcomeMessage?: string;
  summary?: string;
  shotsSummary?: string;
  styleName?: string;      // 风格名称
  artStyle?: string;       // 风格说明
  basePrompt?: string;     // 风格提示词
  bgmUrl?: string;         // 背景音乐URL
  narratorVoiceUrl?: string;  // 旁白音色URL
  highlights: ScriptHighlight[];
  characters: ScriptCharacter[];
  props: ScriptProp[];
  scenes: ScriptScene[];
  shots: ScriptShot[];

  generationErrorMessage?: string;
}


// ============================================
// 分步骤生成流程相关类型定义
// ============================================

/**
 * 步骤状态枚举
 */
export enum StepStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  NEEDS_REVIEW = 'needs_review'
}

/**
 * 脚本生成步骤上下文类型（扩展 ChatContextType）
 */
export enum ScriptStepContextType {
  STORY_SUMMARY = 'script_story_summary',      // Step 1
  VISUAL_STYLE = 'script_visual_style',        // Step 2
  CHARACTERS = 'script_characters',            // Step 3
  PROPS = 'script_props',                      // Step 4
  SCENES = 'script_scenes',                    // Step 5
  SHOTS = 'script_shots'                       // Step 6
}

/**
 * 步骤定义
 */
export interface ScriptStep {
  stepNumber: number;
  name: string;
  status: StepStatus;
  completedAt?: Date;
  data?: StepData;
}

/**
 * 步骤数据联合类型
 */
export type StepData =
  | StorySummaryData
  | VisualStyleData
  | CharactersData
  | PropsData
  | ScenesData
  | ShotsData;

/**
 * Step 1: 故事概要数据
 */
export interface StorySummaryData {
  title: string;
  summary: string;
  highlights: ScriptHighlight[];
  language: ScriptLanguage;
}

/**
 * Step 2: 视觉风格数据
 */
export interface VisualStyleData {
  styleName: string;
  artStyle: string;
  basePrompt: string;
  bgmId?: string;
  bgmUrl?: string;
  narratorVoiceUrl?: string;
}

/**
 * Step 3: 角色数据
 */
export interface CharactersData {
  characters: ScriptCharacter[];
}

/**
 * Step 4: 道具数据
 */
export interface PropsData {
  props: ScriptProp[];
}

/**
 * Step 5: 场景数据
 */
export interface ScenesData {
  scenes: ScriptScene[];
}

/**
 * Step 6: 分镜数据
 */
export interface ShotsData {
  shots: ScriptShot[];
}

// ============================================
// 步骤 AI 响应接口
// ============================================

/**
 * 步骤 AI 响应基础接口
 */
export interface StepAIResponseBase {
  message: string;
}

/**
 * Step 1 AI 响应：故事概要
 */
export interface StorySummaryAIResponse extends StepAIResponseBase {
  content?: StorySummaryData;
}

/**
 * Step 2 AI 响应：视觉风格
 */
export interface VisualStyleAIResponse extends StepAIResponseBase {
  content?: VisualStyleData;
}

/**
 * Step 3-5 AI 响应：实体操作
 */
export interface EntityActionCard {
  actionType: 'create' | 'update' | 'delete';
  entityId?: string;
  name: string;
  prompt?: string;
  description?: string;
  imageUrl?: string;
}

export interface EntityAIResponse extends StepAIResponseBase {
  actions: EntityActionCard[];
}

/**
 * Step 6 AI 响应：分镜操作
 */
export interface ShotActionCard {
  actionType: 'create' | 'update' | 'delete' | 'reorder';
  shotIndex?: number;
  shot?: ScriptShot;
}

export interface ShotsAIResponse extends StepAIResponseBase {
  actions: ShotActionCard[];
}

// ============================================
// 步骤重新生成请求/响应
// ============================================

/**
 * 步骤重新生成请求
 */
export interface RegenerateStepRequest {
  stepNumber: number;
  prompt?: string;
  preserveDownstream?: boolean;
}

/**
 * 步骤重新生成选项
 */
export interface RegenerateStepOptions {
  /** 用户提示词，用于指导 AI 生成 */
  prompt?: string;
  /** 脚本模式：灵活模式或严格模式 */
  mode: ScriptMode;
  /** 是否保留下游步骤数据，默认为 false */
  preserveDownstream?: boolean;
  /** 用户输入的部分步骤数据 */
  userInput?: Partial<StepData>;
}

/**
 * 步骤重新生成响应
 */
export interface RegenerateStepResponse {
  success: boolean;
  stepData: StepData;
  affectedSteps: number[];
  message?: string;
}

// ============================================
// 剧本创建流程重构 - 新增类型定义
// ============================================

/**
 * 创建 Episode 和 EpisodeScript 请求
 * POST /api/user/episode-scripts/create-episode
 * 
 * 用于快速创建 Episode 和 EpisodeScript 记录，不触发生成流程
 * 注意：与 episode.ts 中的 CreateEpisodeRequest 不同，此接口会同时创建 EpisodeScript
 */
export interface CreateEpisodeWithScriptRequest {
  /** 用户需求描述/创意提示词 */
  prompt: string;
  /** 剧本文件URL（docx或txt）- FILE_SCRIPT 模式使用 */
  scriptFileUrl?: string;
  /** 项目剧本章节ID - PROJECT_SCRIPT 模式使用 */
  projectScriptId?: string;
  /** 项目ID（可选，如果提供则不创建新项目） */
  projectId?: string;
  /** 图片生成模型ID */
  imageModelId?: string;
  /** LLM模型ID */
  llmModelId?: string;
  /** 视觉LLM模型ID */
  llmVlModelId?: string;
  /** 画面比例 */
  aspectRatio?: AspectRatio;
  /** 分辨率 */
  resolution?: Resolution;
  /** 用户预先指定的角色 */
  characters?: ScriptCharacter[];
  /** 用户预先指定的道具 */
  props?: ScriptProp[];
  /** 用户预先指定的场景 */
  scenes?: ScriptScene[];
  /** 用户指定的风格 */
  artStyle?: string;
  /** MV 模式：上传的音频文件 URL */
  audioUrl?: string;
  /** MV 模式：音频文件名 */
  audioName?: string;
  /** 用户期望视频时长（秒），如 15/30/60/90/120，不传则由 AI 自动决定 */
  targetDuration?: number;
  /** 单个分镜最大时长（秒），如 2.5/5/7.5/10/12.5/15，不传则不限制 */
  maxShotDuration?: number;
}

/**
 * 创建 Episode 和 EpisodeScript 响应
 */
export interface CreateEpisodeWithScriptResponse {
  /** 创建的 Episode ID */
  episodeId: string;
  /** 创建的 EpisodeScript ID */
  scriptId: string;
  /** 关联的 Project ID */
  projectId: string;
}

/**
 * 启动生成请求
 * POST /api/user/episode-scripts/:scriptId/start-generation
 * 
 * 用于启动剧本生成流程（从 Step 1 开始）
 */
export interface StartGenerationRequest {
  /** 可选：覆盖创建时的 LLM 模型ID */
  llmModelId?: string;
}

/**
 * 启动生成响应
 */
export interface StartGenerationResponse {
  /** Bull 任务ID */
  taskId: string;
  /** 剧本ID */
  scriptId: string;
  /** 生成状态 */
  generationStatus: ScriptGenerationStatus;
}

/**
 * Step 1 故事线生成进度数据
 * 
 * 用于通过 WebSocket 实时推送 Step 1 的流式进度
 * 继承自 ScriptProgressData 基础结构
 */
export interface Step1ProgressData {
  /** 进度类型标识 */
  type: 'step1_storyline';
  /** 剧本ID */
  scriptId: string;
  /** 剧集ID */
  episodeId: string;
  /** 用户ID */
  userId: string;
  /** 当前正在生成的字段 */
  field?: 'language' | 'title' | 'welcomeMessage' | 'summary' | 'highlights';
  /** 增量内容（流式输出） */
  delta?: string;
  /** 完整内容 */
  fullContent?: string;
  /** 该字段是否生成完成 */
  isComplete?: boolean;
  /** 意图识别结果：剧本模式 */
  scriptMode?: ScriptMode;
}
